import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { trapFocus } from '../../ts/helper';

/**
 * Accessible Overlay (A11Y)
 * @requires {@link enableBodyScroll}
 * @requires {@link disableBodyScroll}
 * @requires {@link trapFocus}
 * @example
 *   const myOverlay = new Overlay();
 *   myOverlay.create('Foo', bar, 'Schließen');
 *   myOverlay.close();
 */
export class Overlay {
  connectedCallback() {
    /** @type {HTMLElement} Overlay element */
    this.el = undefined;

    /** @type {HTMLButtonElement} Close button */
    this.closeButton = undefined;
  }

  /**
   * Close and remove {@link Overlay}
   * @returns {void}
   */
  close() {
    enableBodyScroll(this.el);
    this.el.remove();
  }

  /**
   * Create overlay
   * @param {string} type Type of child in overlay -> image | iframe
   * @param {string} label Label describing overlay element
   * @param {HTMLElement} child Any HTML Element that is the content of the overlay
   * @param {string=} closeButtonText Text on close button (`'Close'` by default)
   * @returns {void}
   */
  create(type, label, child, closeButtonText) {
    if (!type) throw Error("Missing required argument 'type'");
    if (type && typeof type !== 'string') {
      throw TypeError(
        `Wrong type given, expected argument 'type' to be of type 'string' but received '${typeof type}'`
      );
    }
    if (!label) throw Error("Missing required argument 'label'");
    if (label && typeof label !== 'string') {
      throw TypeError(
        `Wrong type given, expected argument 'label' to be of type 'string' but received '${typeof label}'`
      );
    }
    if (closeButtonText && typeof closeButtonText !== 'string') {
      throw TypeError(
        `Wrong type given, expected argument 'closeButtonText' to be of type 'string' but received '${typeof closeButtonText}'`
      );
    }
    if (child && !(child instanceof window.HTMLElement)) {
      throw TypeError(
        `Expected argument 'child' to be an instance of 'HTMLElement' but was '${child.constructor.name}'`
      );
    }

    /** Create element */
    this.el = document.createElement('div');
    this.el.setAttribute('is', 'element-overlay');
    this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-modal', 'true');

    const labelId = 'overlay-label';
    const labelEl = document.createElement('p');
    labelEl.innerText = label;
    labelEl.classList.add('label');
    labelEl.id = labelId;

    const wrapper = document.createElement('div');
    wrapper.classList.add('wrapper');
    wrapper.append(labelEl);

    this.el.append(wrapper);
    this.el.setAttribute('aria-labelledby', labelId);

    /**
     * On Keydown
     * @param {KeyboardEvent} e Keydown event
     * @returns {void}
     */
    const onKeyDown = (e) => {
      if (e.code === 'Escape') {
        this.close();
      }
    };

    /** Create close button */
    this.closeButton = document.createElement('button');
    this.closeButton.innerHTML = closeButtonText || 'Close';
    this.closeButton.addEventListener('click', () => {
      this.close();
    });
    this.closeButton.classList.add('close');
    this.closeButton.classList.add('button');
    this.closeButton.classList.add('button--primary');
    this.el.append(this.closeButton);

    if (child) {
      const childWrapper = document.createElement('div');
      childWrapper.classList.add('innerWrapper');
      childWrapper.append(child);
      if (type === 'iframe') {
        childWrapper.classList.add('innerWrapper--video');
      } else if (type === 'image') {
        childWrapper.classList.add('innerWrapper--image');
      }
      wrapper.append(childWrapper);
    }

    document.addEventListener('keydown', onKeyDown);
    document.body.append(this.el);
    disableBodyScroll(this.el);
    trapFocus(this.el);
    labelEl.tabIndex = 0;
    labelEl.focus();
  }
}
